import { ApolloError } from '@apollo/client';
import { SetOptional } from 'type-fest';

import {
  FormModal,
  FormModalProps,
} from '@/components/modals/FormModal/FormModal';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions/FormModalActions';
import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import {
  FormProvider,
  useForm,
  useFormContext,
  useSubmitSuccessHandler,
} from '@/components/react-hook-form';
import { getUserFacingErrorMessages } from '@/graphql/errors';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';
import {
  TenantKind,
  TenantProductTier,
  TenantUsageBucket,
} from '@/types/schema';
import { getEnvironment } from '@/utils/environmentUtils';
import { getFullyQualifiedTenantURL } from '@/utils/tenantUtils';

import { useCreateTenantMutation } from '../graphql/AdminTenantsPage.generated';
import { CreateTenantForm } from './CreateTenantForm';
import { CreateTenantFormShape } from './CreateTenantForm.types';

type FormModalOptionalProps = SetOptional<FormModalProps, 'heading'>;

function CreateTenantModalComponent({
  onClose,
  heading = 'Create new tenant',
  ...formModalProps
}: FormModalOptionalProps) {
  const env = getEnvironment();
  const { showFeedback } = useFeedback();
  const { formRef, handleSave } = useFormSaveHandler();
  const {
    handleSubmit,
    reset,
    shouldBlockNavigation,
    formState,
    getValues,
    setError,
  } = useFormContext<CreateTenantFormShape>();

  const [createTenant] = useCreateTenantMutation();

  const onSubmit = handleSubmit(async (formData) => {
    try {
      await createTenant({
        variables: {
          input: {
            kind: formData.kind,
            name: formData.name,
            subdomain: formData.subdomain,
            usageBucket: formData.usageBucket,
            addonAssetProviderIntegrationEnabled:
              formData.addonAssetProviderIntegrationEnabled,
            productTier: formData.tier,
            trialEndDate: formData.trialEndDate,
          },
        },
      });
    } catch (error) {
      const userFacingMessages =
        error instanceof ApolloError ? getUserFacingErrorMessages(error) : [];
      showFeedback(
        userFacingMessages.length > 0
          ? userFacingMessages.join('. ')
          : FeedbackMessages.formSaveError
      );
      // Keep submission unsuccessful without an unhandled promise rejection.
      // React Hook Form clears root errors before the next submission.
      setError('root.server', { type: 'server' });
    }
  });

  const closeModal = () => {
    reset();
    onClose();
  };

  useSubmitSuccessHandler(() => {
    const formData = getValues();
    const tenantUrl = getFullyQualifiedTenantURL(formData.subdomain);
    showFeedback(`Successfully created a new ${env} tenant!`, {
      alertTargetUrl: tenantUrl,
      variant: 'success',
    });
    closeModal();
  });

  return (
    <FormModal
      {...formModalProps}
      onClose={onClose}
      heading={heading}
      shouldBlockClose={shouldBlockNavigation}
      actions={
        <FormModalActions.Provider formState={formState}>
          <FormModalActions.CancelButton onClick={closeModal} />
          <FormModalActions.SaveButton onClick={handleSave}>
            Create tenant
          </FormModalActions.SaveButton>
        </FormModalActions.Provider>
      }
    >
      <form ref={formRef} onSubmit={onSubmit} noValidate>
        <CreateTenantForm />
      </form>
    </FormModal>
  );
}

export const CreateTenantModal = (props: FormModalOptionalProps) => {
  const formMethods = useForm<CreateTenantFormShape>({
    defaultValues: {
      name: '',
      subdomain: '',
      addonAssetProviderIntegrationEnabled: false,
      tier: TenantProductTier.Pro,
      trialEndDate: null,
      kind: '' as TenantKind,
      usageBucket: '' as TenantUsageBucket,
    },
  });

  return (
    <FormProvider {...formMethods}>
      <CreateTenantModalComponent {...props} />
    </FormProvider>
  );
};
